import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, X } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { AppUrls } from "../app-urls";
import { useMsal } from "@azure/msal-react";
import envConfig from "../../config/config";



// Define available vertical options
const verticalOptions = [
    "Medical", "Industial Products", "DMS", "PE", "CTO", "DPS",
    "HiTech", "Next Gen Communications", "Transportation", "V&V/Emb",
    "Consumer Electronics",
];

// ApiList Component
const ApiList = ({ swaggerFileId, projectId, setShowApiListModal, onClose }) => {
    // Local state
    const [apiData, setApiData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [port, setPort] = useState()
    const [upstreamType, setUpstreamType] = useState("");
    const { accounts } = useMsal();

    // Handle change in base URL input
    const handleBaseUrlChange = (e) => {
        setBaseUrl(e.target.value);
    };
    const handlePortChange = (e) => {
        setPort(e.target.value);
    };

    const handleupstreamTypeChange = (e) => {
        setUpstreamType(e.target.value);
    }

    // Fetch API data and categories
    useEffect(() => {
        if (!swaggerFileId) {
            alert("No swaggerFileId provided");
            return;
        }

        // Fetch API list based on swaggerFileId
        const fetchApiList = async () => {
            try {
                const res = await axios.get(
                    `${envConfig.REACT_APP_APIREPO_WRITE}/apirepo/v0.1/apisSwagger/${swaggerFileId}`
                );
                if (res.data?.apis?.length) {
                    const enrichedData = res.data.apis?.map((api) => ({
                        ...api,
                        isSelected: true, // Auto-select all
                        apiName: api.apiName || "",
                        description: api.description || "",
                        category: api.category || "",
                        vertical: api.vertical || "",
                    }));
                    setApiData(enrichedData);
                } else {
                    alert("No APIs found for this swagger file.");
                }
            } catch (err) {
                console.error("Failed to fetch APIs:", err);
            } finally {
                setLoading(false);
            }
        };

        // Fetch categories
        const fetchCategories = async () => {
            try {
                const res = await axios.get(
                    `${envConfig.REACT_APP_APIREPO_READ}/apirepo/v0.1/uds/apis`
                );
                const tags = res.data?.data?.flatMap((item) => item.tag || []);
                const uniqueTags = [...new Set(tags)];
                setCategories(uniqueTags);
            } catch (err) {
                console.error("Failed to fetch categories:", err);
            }
        };

        fetchApiList();
        fetchCategories();
    }, [swaggerFileId]);

    // Handle changes to API data fields
    const handleChange = (index, field, value) => {
        const updated = [...apiData];
        updated[index][field] = value;
        setApiData(updated);
        setErrorMsg("");
    };

    // Validate the form before submission
    const isFormValid = () => {
        // Validate and strip protocol
        // const isValidUrl = (url) => {
        //     try {
        //         const parsedUrl = new URL(url);
        //         setBaseUrl(parsedUrl.hostname); // remove protocol by keeping only the hostname
        //         return true;
        //     } catch (e) {
        //         return false;
        //     }
        // };

        return (
            baseUrl.trim() !== "" && // not empty
            // isValidUrl(baseUrl) &&   // validate and clean
            apiData.length > 0 &&
            apiData.every(
                (api) =>
                    api.isSelected &&
                    api.apiName.trim() &&
                    api.description.trim() &&
                    api.vertical &&
                    api.category
            )
        );
    };


    // Handle form submission
    const handleSubmit = async () => {
        if (!isFormValid()) {
            setErrorMsg("Please select all rows and fill in all fields.");
            return;
        }

        setErrorMsg("");
        setSubmitLoading(true);

        const payload = {
            swaggerFileId,
            apiDetails: apiData?.map(({ apiId, apiName, description, category, vertical }) => ({
                apiId,
                apiName,
                description,
                category,
                vertical,
            })),
        };

        try {
            // Step 1: Update API Details
            const res = await axios.put(
                `${envConfig.REACT_APP_APIREPO_WRITE}/apirepo/v0.1/updateApiDetails`,
                payload
            );

            if (res.status !== 200) {
                throw new Error("Unexpected response from updateApiDetails.");
            }

            // Step 2: Add gateway data to project
            const gatewayRes = await axios.put(
                `${envConfig.REACT_APP_APIREPO_GATEWAY}/apirepo/v0.1/gatewayshim`,
                {
                    projectId,
                    useremail: accounts[0]?.username.toLowerCase(),
                    nodes: {
                        baseEndpoint: `${baseUrl}:${port}`,
                        weight: 100
                    },
                    "upstreamtype": upstreamType,
                    plugins: {}
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    }
                }
            );

            if (gatewayRes.status !== 200) {
                toast.error("Failed to add API to project.");
                return;
            }

            toast.success("API Added Successfully");

            const reqBody = {
                status: {
                    currentStatus: "pending",
                },
            };

            const statusUpdateRes = await axios.put(
                `${AppUrls.createProject}/detail/${projectId}`,
                reqBody
            );

            if (statusUpdateRes.status === 200) {
                const email = accounts[0]?.username.toLowerCase();
                await axios.post(
                    `${envConfig.REACT_APP_APIREPO_WRITE}/apirepo/v0.1/sendmail/request`,
                    {
                        email,
                        action: "project_add"
                    }
                );
                toast.success("Project status updated to 'review'");
                setShowApiListModal(false);
                onClose();
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                toast.error("Failed to update project status.");
            }

        } catch (err) {
            console.error("Submission failed:", err);
            setErrorMsg("Failed to update API data or gateway data.");
        } finally {
            // Always reset the submit button
            setSubmitLoading(false);
        }
    };



    // Render the component
    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex justify-between">
                <h2 className="text-2xl font-bold mb-4">Review & Submit API Info</h2>
                <button
                    className=" text-gray-500 hover:text-red-600"
                    onClick={() => setShowApiListModal(false)}
                >
                    <X className="w-8 h-8" />
                </button>
            </div>


            {errorMsg && (
                <div className="mb-4 text-red-600 font-semibold bg-red-100 p-2 rounded">
                    {errorMsg}
                </div>
            )}

            {loading ? (
                <p>Loading APIs...</p>
            ) : (
                <div>
                    <div className="overflow-x-auto shadow rounded-lg border max-h-[290px] border-gray-200">
                        <table className="min-w-full bg-white text-sm text-left">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="p-3">Select</th>
                                    <th className="p-3">URL</th>
                                    <th className="p-3">Method</th>
                                    <th className="p-3">API Name</th>
                                    <th className="p-3">Description</th>
                                    <th className="p-3">Vertical</th>
                                    <th className="p-3">API Category</th>
                                    <th className="p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiData?.map((api, index) => {
                                    return (
                                        <tr key={api.apiId} className="border-t">
                                            <td className="p-3 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={api.isSelected}
                                                    readOnly
                                                    className="w-4 h-4 cursor-not-allowed"
                                                />
                                            </td>
                                            <td className="p-3 font-mono text-blue-600">{api.url}</td>
                                            <td className="p-3 uppercase">{api.method}</td>
                                            <td className="p-3">
                                                <input
                                                    type="text"
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                    value={api.apiName}
                                                    onChange={(e) =>
                                                        handleChange(index, "apiName", e.target.value)
                                                    }
                                                />
                                            </td>
                                            <td className="p-3">
                                                <input
                                                    type="text"
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                    value={api.description}
                                                    onChange={(e) =>
                                                        handleChange(index, "description", e.target.value)
                                                    }
                                                />
                                            </td>
                                            <td className="p-3">
                                                <select
                                                    value={api.vertical}
                                                    onChange={(e) =>
                                                        handleChange(index, "vertical", e.target.value)
                                                    }
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                >
                                                    <option value="">Select Vertical</option>
                                                    {verticalOptions?.map((v, idx) => (
                                                        <option key={idx} value={v}>
                                                            {v}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-3">
                                                <select
                                                    value={api.category}
                                                    onChange={(e) =>
                                                        handleChange(index, "category", e.target.value)
                                                    }
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories?.map((cat, idx) => (
                                                        <option key={idx} value={cat}>
                                                            {cat}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-3 text-center">
                                                {api.apiName && api.description && api.vertical && api.category ? (
                                                    <CheckCircle className="text-green-500 w-5 h-5 mx-auto" />
                                                ) : (
                                                    <XCircle className="text-red-500 w-5 h-5 mx-auto" />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>


                    {/* Base URL input */}
                    <div className="mt-6 flex items-center flex-row gap-5">
                        <div className="flex items-center flex-row gap-2">
                            <label className="block text-sm font-semibold  text-gray-700 mb-1">
                                Base URL
                            </label>
                            <input
                                type="text"
                                value={baseUrl}
                                onChange={handleBaseUrlChange}
                                placeholder="your-api-base.com"
                                className="border rounded px-3 py-2 text-sm"
                            />
                        </div>
                        <div className="flex items-center flex-row gap-2">
                            <label className="block text-sm font-semibold  text-gray-700 mb-1">
                                PORT
                            </label>
                            <input
                                type="number"
                                value={port}
                                onChange={handlePortChange}
                                placeholder="443"
                                className="border rounded px-3 py-2 text-sm"
                            />
                        </div>
                        <div className="flex items-center flex-row gap-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                UpStream Type
                            </label>
                            <select
                                value={upstreamType}
                                onChange={handleupstreamTypeChange}
                                className="border rounded px-3 py-2 text-sm"
                            >
                                <option value="">Select an option</option>
                                <option value="roundrobin">Round Robin</option>
                                <option value="leastconn">Least Conn</option>
                            </select>
                        </div>

                    </div>
                </div>
            )}

            {/* Submit Button */}
            {!loading && (
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSubmit}
                        disabled={!isFormValid() || submitLoading}
                        className={`px-6 py-2 rounded text - white font - medium ${!isFormValid() || submitLoading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                            }`}
                    >
                        {submitLoading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ApiList;
